import { AlertCircle, AlertTriangle, Info, Minus } from 'lucide-react';

interface PriorityBadgeProps {
  priority: string;
}

type CanonicalPriority = 'Critical' | 'High' | 'Medium' | 'Low';

// normalize case and unknown values so the badge never renders blank —
// upstream sources mix 'High' (recommendations) and 'high' (findings)
function canonicalize(priority: string): CanonicalPriority {
  switch (priority?.toLowerCase()) {
    case 'critical':
      return 'Critical';
    case 'high':
      return 'High';
    case 'low':
      return 'Low';
    default:
      return 'Medium';
  }
}

export const PriorityBadge: React.FC<PriorityBadgeProps> = ({ priority }) => {
  const canonical = canonicalize(priority);

  const colorMap: Record<CanonicalPriority, string> = {
    Critical: 'bg-rose-500/90 text-slate-50 shadow-lg shadow-rose-500/20',
    High: 'bg-orange-500/90 text-slate-50 shadow-lg shadow-orange-500/20',
    Medium: 'bg-amber-500/90 text-slate-50 shadow-lg shadow-amber-500/20',
    Low: 'bg-slate-400/90 text-slate-50 shadow-lg shadow-slate-400/20',
  };

  const iconMap: Record<CanonicalPriority, typeof Info> = {
    Critical: AlertCircle,
    High: AlertTriangle,
    Medium: Info,
    Low: Minus,
  };

  const Icon = iconMap[canonical];

  return (
    <span
      className={`px-2 py-1 rounded text-xs font-semibold flex items-center gap-1 ${colorMap[canonical]}`}
    >
      <Icon className="w-3 h-3" />
      {canonical}
    </span>
  );
};
